import { useRef, useCallback, CSSProperties } from "react";
import { styled } from "~/ui/style/stitches.config";

/**
 * HoloCard - Adds 3D tilt and glare effect to any content
 * Based on https://github.com/simeydotme/pokemon-cards-css
 *
 * Features:
 * - 3D tilt that follows pointer position
 * - Glare overlay that follows pointer
 * - Spring physics settle animation on pointer leave
 */

// Spring physics for settle animation
const springSettle = (
  el: HTMLElement,
  startX: number,
  startY: number,
  targetX: number = 0,
  targetY: number = 0,
  onComplete?: () => void
) => {
  const stiffness = 0.03;
  const damping = 0.7;
  let velocityX = 0;
  let velocityY = 0;
  let currentX = startX;
  let currentY = startY;

  const animate = () => {
    // Spring toward target values instead of 0
    const forceX = stiffness * (targetX - currentX);
    const forceY = stiffness * (targetY - currentY);
    velocityX = (velocityX + forceX) * damping;
    velocityY = (velocityY + forceY) * damping;
    currentX += velocityX;
    currentY += velocityY;

    el.style.setProperty("--rotate-x", `${currentY}deg`);
    el.style.setProperty("--rotate-y", `${currentX}deg`);

    const distX = Math.abs(currentX - targetX);
    const distY = Math.abs(currentY - targetY);

    if (
      distX > 0.01 ||
      distY > 0.01 ||
      Math.abs(velocityX) > 0.01 ||
      Math.abs(velocityY) > 0.01
    ) {
      requestAnimationFrame(animate);
    } else {
      el.style.setProperty("--rotate-x", `${targetY}deg`);
      el.style.setProperty("--rotate-y", `${targetX}deg`);
      onComplete?.();
    }
  };

  requestAnimationFrame(animate);
};

interface HoloCardProps {
  children: React.ReactNode;
  className?: string;
  style?: CSSProperties;
  width?: number | string;
  height?: number | string;
  onClick?: () => void;
  /** Resting X rotation in degrees (tilt forward/back like on a table) */
  restingRotateX?: number;
  /** Disable pointer interactions (useful during flip animations) */
  disableInteraction?: boolean;
  /** Border radius for clipping the glare effect */
  borderRadius?: number;
}

export const HoloCard = ({
  children,
  className,
  style,
  width = "100%",
  height = "100%",
  onClick,
  restingRotateX = 0,
  disableInteraction = false,
  borderRadius,
}: HoloCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);

  // Set initial resting rotation
  const initialStyle = {
    "--rotate-x": `${restingRotateX}deg`,
    "--rotate-y": "0deg",
  } as CSSProperties;

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = cardRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const percentX = Math.round((x / rect.width) * 100);
      const percentY = Math.round((y / rect.height) * 100);
      const rotateX = Math.round((percentY - 50) / -2.5);
      const rotateY = Math.round((percentX - 50) / 2.5);

      el.style.setProperty("--pointer-x", `${percentX}%`);
      el.style.setProperty("--pointer-y", `${percentY}%`);
      el.style.setProperty("--rotate-x", `${rotateX}deg`);
      el.style.setProperty("--rotate-y", `${rotateY}deg`);
      el.style.setProperty("--glare-opacity", "1");
    },
    []
  );

  const handlePointerLeave = useCallback(() => {
    const el = cardRef.current;
    if (!el) return;

    const currentRotateY = parseFloat(
      el.style.getPropertyValue("--rotate-y") || "0"
    );
    const currentRotateX = parseFloat(
      el.style.getPropertyValue("--rotate-x") || "0"
    );

    // Just fade out glare at current position (don't move it to center)
    el.style.setProperty("--glare-opacity", "0");

    // Spring back to resting position
    springSettle(el, currentRotateY, currentRotateX, 0, restingRotateX);
  }, [restingRotateX]);

  // Prevent parent scroll while interacting with the card
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!disableInteraction && e.cancelable) {
      e.preventDefault();
    }
  }, [disableInteraction]);

  return (
    <CardWrapper
      ref={cardRef}
      className={className}
      style={{ width, height, ...style, ...initialStyle }}
      onPointerMove={disableInteraction ? undefined : handlePointerMove}
      onPointerLeave={handlePointerLeave}
      onTouchMove={handleTouchMove}
      onClick={onClick}
    >
      <CardRotator
        style={
          borderRadius
            ? { borderRadius, overflow: "hidden" }
            : undefined
        }
      >
        {children}
        {!disableInteraction && <CardGlare />}
      </CardRotator>
    </CardWrapper>
  );
};

// Outer wrapper stays flat - provides stable hit area for pointer events
const CardWrapper = styled("div", {
  "--pointer-x": "50%",
  "--pointer-y": "50%",
  "--rotate-x": "0deg",
  "--rotate-y": "0deg",
  "--glare-opacity": "0",

  position: "relative",
  cursor: "pointer",
  touchAction: "none",
});

// Inner element does the 3D transform - this way the hit area doesn't move
const CardRotator = styled("div", {
  width: "100%",
  height: "100%",
  position: "relative",
  transformStyle: "preserve-3d",
  transform:
    "perspective(600px) rotateX(var(--rotate-x)) rotateY(var(--rotate-y))",
  transition: "transform 0.1s ease-out",
  willChange: "transform",
  pointerEvents: "none",
});

const CardGlare = styled("div", {
  position: "absolute",
  inset: 0,
  borderRadius: "8px",
  pointerEvents: "none",
  mixBlendMode: "overlay",
  opacity: "var(--glare-opacity)",
  transition: "opacity 0.3s ease-out",
  background: `radial-gradient(
    farthest-corner circle at var(--pointer-x) var(--pointer-y),
    hsla(0, 0%, 100%, 0.8) 10%,
    hsla(0, 0%, 100%, 0.5) 25%,
    transparent 60%
  )`,
});


